<template>
  <div class="date-range-wrapper" data-testid="lb-date-picker">
    <v-btn
      class="date-range-trigger text-none"
      @click="openDialog"
      data-testid="lb-date-picker__trigger"
    >
      <img src="/img/icons/fbc-icons/DateRange.svg" alt="Date Range Icon" class="date-range-trigger-icon mr-2">
      <transition name="date-range-summary-fade" mode="out-in">
        <span :key="triggerLabelKey">{{ formattedRange || 'Select Date Range' }}</span>
      </transition>
    </v-btn>

    <v-dialog
      v-model="dialog"
      width="380"
      content-class="date-range-dialog"
    >
      <v-card class="date-range-card">
        <div class="picker-header">
          <div class="text-h5 font-weight-bold">
            {{ displayMonth }}
          </div>

          <div>
            <v-btn icon small @click="previousMonth">
              <v-icon color="white">mdi-chevron-left</v-icon>
            </v-btn>

            <v-btn icon small @click="nextMonth">
              <v-icon color="white">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="cycle-info px-5 py-3">
          <div class="d-flex align-center">
            <div class="range-line mr-2"></div>

            <div>
              <div class="cycle-info__item">
                <strong>Cycle:</strong>
                {{ cycleRange }}
              </div>
              
              <div class="cycle-info__item">
                Max {{ maxDays }} days selectable per view
              </div>
            </div>
          </div>
        </div>

        <v-divider />

        <v-date-picker
          v-model="selected"
          :picker-date.sync="pickerDate"
          :allowed-dates="allowedDates"
          range
          no-title
          class="custom-picker date-range-picker-calendar"
        />

        <v-divider />

        <v-card-actions class="px-4 py-3">
          <transition name="date-range-summary-fade" mode="out-in">
            <span
              :key="summaryKey"
              class="font-weight-medium date-range-summary"
            >
              {{ formattedRange || 'Select dates' }}
            </span>
          </transition>

          <span v-if="rangeError" class="error--text caption ml-2">{{ rangeError }}</span>

          <v-spacer />

          <v-btn
            outlined
            color="#123847"
            @click="cancel"
          >
            Cancel
          </v-btn>

          <v-btn
            color="#123847"
            dark
            @click="apply"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  name: "DateRangePicker",

  props: {
    value: {
      type: [Array, String],
      default: () => []
    },
    minDays: {
      type: Number,
      default: 1
    },
    maxDays: {
      type: Number,
      default: 15
    },
    cycleStart: {
      type: String,
      default: ''
    },
    cycleEnd: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      dialog: false,
      selected: [],
      pickerDate: dayjs().format("YYYY-MM"),
      rangeError: "",
      didApply: false,
    };
  },

  computed: {
    displayMonth() {
      return dayjs(`${this.pickerDate}-01`).format("MMMM YYYY");
    },

    cycleRange() {
      if (this.cycleStart && this.cycleEnd) {
        const start = dayjs(this.cycleStart).format('MMM D');
        const end = dayjs(this.cycleEnd).format('MMM D');
        return `${start} — ${end}`;
      }
      return '';
    },

    formattedRange() {
      if (!this.selected?.length) {
        if (typeof this.value === "string") {
          return this.value;
        }
        return "";
      }

      const start = dayjs(this.selected[0]).format("MMM D");

      const end = this.selected[1]
        ? dayjs(this.selected[1]).format("MMM D")
        : "";

      return `${start} — ${end}`;
    },

    summaryKey() {
      if (!this.selected?.length) return 'empty';
      return this.selected.join('|');
    },

    triggerLabelKey() {
      if (this.dialog) return this.summaryKey;
      if (Array.isArray(this.value) && this.value.length) {
        return this.value.join('|');
      }
      return 'empty';
    }
  },

  methods: {
    normalizeRange(range) {
      if (!Array.isArray(range) || range.length === 0) {
        return [];
      }

      if (range.length === 1) {
        return [range[0]];
      }

      const [a, b] = range;
      if (!a || !b) {
        return range.filter(Boolean);
      }

      return dayjs(a).isBefore(dayjs(b)) ? [a, b] : [b, a];
    },
    allowedDates(date) {
      if (!Array.isArray(this.selected) || this.selected.length !== 1) {
        return true;
      }

      const anchor = this.selected[0];
      if (!anchor) {
        return true;
      }

      const diff = Math.abs(dayjs(date).diff(dayjs(anchor), "day"));
      return diff <= this.maxDays - 1;
    },
    enforceMaxDays(range) {
      const normalized = this.normalizeRange(range);
      if (normalized.length < 2) {
        this.rangeError = "";
        return normalized;
      }

      const [start, end] = normalized;
      const dayDiff = dayjs(end).diff(dayjs(start), "day") + 1;

      if (dayDiff < this.minDays) {
        this.rangeError = `Range must be at least ${this.minDays} days`;
        return normalized;
      }

      if (dayDiff <= this.maxDays) {
        this.rangeError = "";
        return normalized;
      }

      const limitedEnd = dayjs(start).add(this.maxDays - 1, "day").format("YYYY-MM-DD");
      this.rangeError = `Range limited to ${this.maxDays} days`;
      return [start, limitedEnd];
    },
    syncSelectedFromValue() {
      if (Array.isArray(this.value)) {
        this.selected = this.enforceMaxDays(this.value);
      } else {
        this.selected = [];
      }
      this.rangeError = "";
      if (this.selected[0]) {
        this.pickerDate = dayjs(this.selected[0]).format("YYYY-MM");
      }
    },
    openDialog() {
      this.syncSelectedFromValue();
      this.dialog = true;
    },
    apply() {
      const normalized = this.normalizeRange(this.selected);
      if (normalized.length === 2) {
        const dayDiff = dayjs(normalized[1]).diff(dayjs(normalized[0]), "day") + 1;
        if (dayDiff < this.minDays) {
          this.rangeError = `Range must be at least ${this.minDays} days`;
          return;
        }
      }
      this.$emit("input", normalized);
      this.$emit("change", { start: normalized[0], end: normalized[1] });
      this.didApply = true;
      this.dialog = false;
    },

    cancel() {
      this.dialog = false;
    },

    previousMonth() {
      this.pickerDate = dayjs(`${this.pickerDate}-01`).subtract(1, "month").format("YYYY-MM");
    },

    nextMonth() {
      this.pickerDate = dayjs(`${this.pickerDate}-01`).add(1, "month").format("YYYY-MM");
    }
  },
  mounted() {
    this.syncSelectedFromValue();
  },
  watch: {
    dialog(isOpen) {
      if (!isOpen) {
        if (!this.didApply) {
          this.syncSelectedFromValue();
        }
        this.didApply = false;
      }
    },
    selected(newValue) {
      if (!Array.isArray(newValue) || newValue.length < 2) {
        this.rangeError = "";
        return;
      }

      this.$nextTick(() => {
        const enforced = this.enforceMaxDays(this.selected);
        const isSame = JSON.stringify(enforced) === JSON.stringify(this.selected);
        if (!isSame) {
          this.selected = enforced;
        }
      });
    },
    value() {
      if (!this.dialog) {
        this.syncSelectedFromValue();
      }
    }
  }
};
</script>

<style scoped>
.date-range-card {
  border-radius: 10px;
  overflow: hidden;
}

.picker-header {
  background: #123847;
  color: white;
  min-height: 5.25rem;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 12px 24px;
}

.cycle-info {
  background: #fff;
}

.range-line {
  width: 14px;
  height: 2px;
  border-radius: 999px;
  background: var(--primary-300, #668499);
}

.custom-picker {
  box-shadow: none !important;
  width: 100%;
}

::v-deep.custom-picker .v-picker__body {
  width: 100% !important;
}

::v-deep.custom-picker .v-date-picker-header {
  padding-left: var(--space-4, 1rem);
  padding-right: var(--space-4, 1rem);
}

.date-range-trigger {
  min-width: 14rem;
  justify-content: flex-start;
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  border-color: #D0D5DD !important;
  background-color: #EDEDED !important;
}

.date-range-trigger span {
  color: #374151 !important;
}

.date-range-summary {
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.date-range-summary-fade-enter-active,
.date-range-summary-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.date-range-summary-fade-enter,
.date-range-summary-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

</style>

<!-- Unscoped: v-dialog detaches to v-app, so styles must not depend on parent wrapper -->
<style lang="scss">
$range-fill: rgba(18, 56, 71, 0.09);
$range-accent: #123847;
$range-ease: cubic-bezier(0.4, 0, 0.2, 1);

.date-range-picker-calendar {
  .v-date-picker-table--date td {
    padding: 0 !important;
  }

  .v-date-picker-table--date .v-btn {
    position: relative;
    z-index: 1;
    transition:
      background-color 0.22s $range-ease,
      color 0.22s $range-ease,
      border-color 0.22s $range-ease,
      box-shadow 0.22s $range-ease,
      opacity 0.22s $range-ease,
      transform 0.18s $range-ease,
      border-radius 0.28s $range-ease,
      width 0.28s $range-ease;

    &::before {
      transition: opacity 0.22s $range-ease, background-color 0.22s $range-ease;
    }
  }

  /* In-between dates: fill strip spans the full cell */
  .v-date-picker-table--date .v-btn.v-btn--active:not(.v-date-picker--first-in-range):not(.v-date-picker--last-in-range) {
    width: 100% !important;
    max-width: none !important;
    border-radius: 0 !important;
    background-color: $range-fill !important;
    border-color: transparent !important;
    box-shadow: none !important;
    color: rgba(0, 0, 0, 0.87) !important;

    &::before {
      opacity: 0 !important;
      background-color: transparent !important;
    }
  }

  /* Start cap */
  .v-date-picker-table--date .v-btn.v-date-picker--first-in-range:not(.v-date-picker--last-in-range) {
    width: 100% !important;
    max-width: none !important;
    border-radius: 999px 0 0 999px !important;
    background-color: $range-accent !important;
    border-color: $range-accent !important;
    color: #fff !important;

    &::before {
      opacity: 0 !important;
    }
  }

  /* End cap */
  .v-date-picker-table--date .v-btn.v-date-picker--last-in-range:not(.v-date-picker--first-in-range) {
    width: 100% !important;
    max-width: none !important;
    border-radius: 0 999px 999px 0 !important;
    background-color: $range-accent !important;
    border-color: $range-accent !important;
    color: #fff !important;

    &::before {
      opacity: 0 !important;
    }
  }

  /* Single anchor date (range not yet complete): circular endpoint only */
  .v-date-picker-table--date .v-btn.v-date-picker--first-in-range.v-date-picker--last-in-range {
    background-color: $range-accent !important;
    border-color: $range-accent !important;
    color: #fff !important;

    &::before {
      opacity: 0 !important;
    }
  }

  /* Disabled dates fade in after first date is picked */
  .v-date-picker-table--date .v-btn.v-btn--disabled {
    opacity: 0.38;
    transition: opacity 0.28s $range-ease;
  }
}

.cycle-info__item {
  color: #828285;
}
</style>